import * as THREE from 'three';
import type { BattleState, MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { captureSky, paintHour, seaMat, stiltHouses, type Sky } from './shore.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The fin (La Orilla 2, docs/SAGAS.md): the stilt-houses again, with the sea in. Past the clearing's rim on the sea side
 * (`-rules.fin.dir`) the sea covers everything, surf breaking on a line of black rocks. Three lanes run in from the sea
 * (`rules.fin.lanes`, `half` wide each side, as sim/battle/fin.ts `inLane`), drawn as wet, wave-worn streaks; two tarred
 * mooring posts stand in each. Far out at sea lies the titan's fore-fin, a dark shape the size of a mountain.
 *
 * When a lane is marked (`s.fin.lane`, `warn` ticks before `s.fin.next`) the fin rises out of the sea streaming water,
 * its shadow runs up the lane from the sea and the lane's edges burn red. On the sweep a wall of white water runs down
 * the lane in about a second, the fin comes down and the camera shakes. Heroes holding a post meanwhile cling to it by a
 * rope; those thrown out splash. Day until `rules.fin.night`, a moonlit night, a warm dawn at `dawn`.
 *
 * La Orilla 5 (`mode`): in its first fight (`breath`) it is the dragon's fire that comes down a lane: when one is
 * marked, the dragon's skull breaks the surface at the lane's sea end, its eyes and throat glowing, the lane's edges
 * smoulder and embers run in along it; on the sweep a sheet of fire races down the lane from its mouth and burns out,
 * and the titan's fin stays down. In the last (`big`) the wave is the wave, but the fin rises higher and closer, and
 * comes down harder.
 *
 * Draw calls: the coast (one merged mesh), the lamps (one), the sea (one), the three lanes (one each), the fin (one),
 * the ropes (one instanced), a glow layer; with `breath`, the dragon's skull (two). No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['fin']>;

const K = WORLD_PER_HEX / 1000;
const LAMP = new THREE.Color('#ffd6a0');
const RED = new THREE.Color('#ff4f3a');
const CLING = new THREE.Color('#dff0ff');
const ROCK = ['#3a3c40', '#46484c', '#2e3034'];
const WET = new THREE.Color('#5a5a54');
const MAX_ROPES = 8;
/** How long the wall of water takes to run down a lane, and the fin to come down (seconds). */
const SWEEP_S = 1.0;

const laneMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -4,
    uniforms: {
      warn: { value: 0 },
      sweep: { value: -1 },
      time: { value: 0 },
      night: { value: 0 },
      fire: { value: 0 },
      red: { value: RED.clone() },
    },
    vertexShader: /* glsl */ `
      attribute vec2 lane;
      varying vec2 vL;
      varying vec2 vXZ;
      void main() {
        vL = lane;
        vXZ = position.xz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying vec2 vL;
      varying vec2 vXZ;
      uniform float warn;
      uniform float sweep;
      uniform float time;
      uniform float night;
      uniform float fire;
      uniform vec3 red;
      void main() {
        // vL.x: 0 at the sea, 1 at the far side; vL.y: -1..1 across.
        float edge = smoothstep(0.72, 1.0, abs(vL.y));
        float streak = 0.5 + 0.5 * sin(vL.x * 60.0 + vL.y * 3.0) * sin(vL.x * 23.0 - vL.y * 5.0);
        float fade = smoothstep(0.0, 0.06, vL.x) * (1.0 - smoothstep(0.88, 1.0, vL.x));
        vec3 col = vec3(0.22, 0.26, 0.28);
        float a = (0.07 + 0.08 * streak + 0.16 * edge) * fade;
        // The dragon's fire (La Orilla 5): the lane smoulders in from the sea, embers running along it; then a sheet of
        // flame races down it and burns out, leaving it scorched a moment.
        if (fire > 0.5) {
          if (warn > 0.0) {
            float heat = 1.0 - smoothstep(warn - 0.15, warn, vL.x);
            float pulse = 0.6 + 0.4 * sin(time * (8.0 + 10.0 * warn));
            float rim = smoothstep(0.72, 0.98, abs(vL.y)) * fade;
            col = mix(col, vec3(0.45, 0.08, 0.02), heat * 0.8);
            a = max(a, 0.35 * heat * fade);
            col = mix(col, mix(red, vec3(1.0, 0.55, 0.2), heat), rim * pulse);
            a = max(a, rim * (0.55 + 0.35 * pulse));
            // Arrows of fire pointing in from the sea, down the middle.
            float chev = step(0.88, fract(vL.x * 9.0 - time * 1.6 + abs(vL.y) * 0.6)) * (1.0 - smoothstep(0.3, 0.45, abs(vL.y)));
            col = mix(col, vec3(1.0, 0.55, 0.22), chev);
            a = max(a, chev * 0.75 * fade);
          }
          if (sweep >= 0.0) {
            float front = sweep * 1.2;
            float lit = 1.0 - smoothstep(front - 0.04, front, vL.x);
            float burn = 1.0 - smoothstep(0.7, 1.5, sweep);
            float n = 0.5 + 0.5 * sin(vL.x * 50.0 + time * 20.0 + vL.y * 5.0) * sin(vL.x * 19.0 - time * 11.0);
            float core = 1.0 - smoothstep(0.0, 0.85, abs(vL.y));
            vec3 flame = mix(vec3(0.85, 0.16, 0.03), mix(vec3(1.0, 0.6, 0.2), vec3(1.0, 0.94, 0.8), core * n), core);
            col = mix(col, flame, lit * burn);
            a = max(a, lit * burn * (0.6 + 0.35 * n) * fade);
            // Scorched behind the flame as it dies.
            float scorch = lit * (1.0 - burn) * (1.0 - smoothstep(1.2, 1.6, sweep));
            col = mix(col, vec3(0.06, 0.04, 0.04), scorch);
            a = max(a, scorch * 0.5 * fade);
          }
          col *= mix(1.0, 0.75, night);
          gl_FragColor = vec4(col, a);
          return;
        }
        // Marked: the fin's shadow runs up the lane from the sea, its edges burn red and pulse.
        if (warn > 0.0) {
          float shade = 1.0 - smoothstep(warn - 0.08, warn, vL.x);
          col = mix(col, vec3(0.02, 0.03, 0.05), shade);
          a = max(a, 0.6 * shade * fade);
          float pulse = 0.6 + 0.4 * sin(time * (8.0 + 10.0 * warn));
          float rim = smoothstep(0.78, 0.98, abs(vL.y)) * fade;
          col = mix(col, red, rim * pulse);
          a = max(a, rim * (0.55 + 0.35 * pulse));
          // Arrows of red pointing in from the sea, down the middle.
          float chev = step(0.9, fract(vL.x * 9.0 - time * 1.2 + abs(vL.y) * 0.6)) * (1.0 - smoothstep(0.25, 0.4, abs(vL.y)));
          col = mix(col, red, chev * 0.8);
          a = max(a, chev * 0.65 * fade);
        }
        // The sweep: a wall of white water running down the lane, the sea behind it draining away.
        if (sweep >= 0.0) {
          float front = sweep * 1.15;
          float wall = smoothstep(front - 0.07, front, vL.x) * (1.0 - smoothstep(front, front + 0.015, vL.x));
          float behind = (1.0 - step(front, vL.x)) * (1.0 - smoothstep(0.6, 1.15, sweep)) ;
          vec3 sea = vec3(0.16, 0.38, 0.44);
          col = mix(col, sea, behind);
          a = max(a, behind * 0.75);
          float lace = 0.6 + 0.4 * sin(vL.y * 30.0 + time * 12.0);
          col = mix(col, vec3(1.0), wall * lace);
          a = max(a, wall * 0.95);
        }
        col *= mix(1.0, 0.6, night);
        gl_FragColor = vec4(col, a);
      }`,
  });

export class FinLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  private sea: THREE.Mesh;
  private seaM: THREE.ShaderMaterial;
  private lanes: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; sea: THREE.Vector3; land: THREE.Vector3 }[] = [];
  private fin: THREE.Mesh;
  private finRest: number;
  private finUp: number;
  private ropes: THREE.InstancedMesh;
  private posts: THREE.Vector3[] = [];
  private lamps: THREE.Vector3[] = [];
  private rim: THREE.Vector3[] = [];
  private sky: Sky;
  private now = 0;
  /** The last sweep: which lane, and when it started (frame clock). */
  private swept: { lane: number; at: number } | null = null;
  private raise = 0;
  private v = new THREE.Vector3();
  private v2 = new THREE.Vector3();
  /** La Orilla 5's first fight: the dragon's skull breaking the surface at a marked lane's sea end. */
  private skull: THREE.Group | null = null;
  /** The sea's surface (world y). */
  get seaY(): number {
    return this.sea.position.y;
  }
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private s3 = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private r: Rules,
    /** La Orilla 5: `breath`, the dragon's fire down the lanes; `big`, the fin over the dragon (a bigger, harder fin). */
    private mode: 'wave' | 'breath' | 'big' = 'wave',
  ) {
    this.group.name = 'fin-coast';
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const ball = new THREE.SphereGeometry(0.5, 8, 6);
    const torus = new THREE.TorusGeometry(0.5, 0.12, 5, 12).rotateX(Math.PI / 2);
    const kit = new Kit();
    const lit = new Kit();
    const rng = mulberry(r.dir.x * 31 + r.dir.y * 17 + 7);
    const R = (): number => rng() * 2 - 1;
    // In from the sea (sim, thousandths) and across it (the sim's lanes are offsets along it).
    const dx = r.dir.x / 1000;
    const dy = r.dir.y / 1000;
    const px = -dy;
    const py = dx;
    const sim = (a: number, off: number): { x: number; y: number } => ({ x: dx * a + px * off, y: dy * a + py * off });
    // Where the sea starts (sim units along the way in, negative: out to sea) and where the land ends.
    const seaAt = -(map.bastion + 0.55) * 1000;
    const landAt = (map.radius + 0.5) * 1000;

    // ---- The stilt-houses on the node, facing the sea.
    stiltHouses(ctx, kit, lit, -dx, -dy, R, this.lamps, { box, cyl, ball });

    // ---- The mooring posts: thick tarred piles with rope rings, a coil at the foot.
    for (const p of r.posts) {
      const w = ctx.at(p.x, p.y, 0);
      const gy = ctx.ground(w.x, w.z);
      this.posts.push(new THREE.Vector3(w.x, gy + 1.2, w.z));
      kit.add(cyl, '#2a2622', place(w.x, gy + 0.62, w.z, 0, new THREE.Vector3(0.26, 1.3, 0.26), 0.04 * R(), 0.04 * R()));
      kit.add(cyl, '#3a342c', place(w.x, gy + 1.29, w.z, 0, new THREE.Vector3(0.3, 0.05, 0.3)));
      for (const h of [0.55, 0.95]) kit.add(torus, '#9a8566', place(w.x, gy + h, w.z, R() * 3, new THREE.Vector3(0.34, 0.6, 0.34)));
      kit.add(torus, '#8a7656', place(w.x + 0.32, gy + 0.05, w.z + 0.1, R() * 3, new THREE.Vector3(0.36, 0.5, 0.36)));
      kit.add(torus, '#8a7656', place(w.x + 0.32, gy + 0.11, w.z + 0.1, R() * 3, new THREE.Vector3(0.28, 0.5, 0.28)));
    }

    // ---- The lanes: wave-worn stones along their edges, and a wet streak drawn as its own mesh each.
    const geoLane = (off: number): { geo: THREE.BufferGeometry; sea: THREE.Vector3; land: THREE.Vector3 } => {
      const NA = 48;
      const NC = 6;
      const pos: number[] = [];
      const lane: number[] = [];
      const idx: number[] = [];
      for (let i = 0; i <= NA; i++) {
        const a = seaAt + ((landAt - seaAt) * i) / NA;
        for (let j = 0; j <= NC; j++) {
          const across = (j / NC) * 2 - 1;
          const s = sim(a, off + across * r.half);
          const w = ctx.at(s.x, s.y, 0);
          pos.push(w.x, ctx.ground(w.x, w.z) + 0.09, w.z);
          lane.push(i / NA, across);
        }
      }
      for (let i = 0; i < NA; i++)
        for (let j = 0; j < NC; j++) {
          const a = i * (NC + 1) + j;
          const b = a + NC + 1;
          idx.push(a, b, a + 1, a + 1, b, b + 1);
        }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('lane', new THREE.Float32BufferAttribute(lane, 2));
      g.setIndex(idx);
      const s0 = sim(seaAt, off);
      const s1 = sim(landAt, off);
      return { geo: g, sea: ctx.at(s0.x, s0.y, 0), land: ctx.at(s1.x, s1.y, 0) };
    };
    for (const off of r.lanes) {
      const { geo, sea, land } = geoLane(off);
      const mat = laneMat();
      const mesh = new THREE.Mesh(geo, mat);
      mesh.name = 'fin-lane';
      mesh.renderOrder = 2;
      mesh.frustumCulled = false;
      this.geos.push(geo);
      this.mats.push(mat);
      this.group.add(mesh);
      this.lanes.push({ mesh, mat, sea, land });
      // Low, wave-worn stones along both edges, and weed caught between them.
      for (let a = seaAt + 500; a < landAt - 600; a += 1100 + 400 * rng()) {
        for (const side of [-1, 1]) {
          const s = sim(a, off + side * (r.half + 120 * R()));
          const w = ctx.at(s.x, s.y, 0);
          kit.add(rock, ROCK[Math.floor(rng() * 3)] as string, place(w.x, ctx.ground(w.x, w.z) + 0.03, w.z, R() * 3, new THREE.Vector3(0.32 + 0.12 * R(), 0.1, 0.26 + 0.1 * R())));
          if (rng() < 0.5) kit.add(rock, '#2f4a2a', place(w.x + 0.2 * R(), ctx.ground(w.x, w.z) + 0.02, w.z + 0.2 * R(), R() * 3, new THREE.Vector3(0.2, 0.03, 0.12)));
        }
      }
    }

    // ---- The sea's edge: black rocks along the rim on the sea side, the surf breaking on them.
    const span = (map.radius + 1) * 1000;
    for (let off = -span; off <= span; off += 650) {
      const a = seaAt + 150 + 250 * R();
      const s = sim(a, off + 150 * R());
      const w = ctx.at(s.x, s.y, 0);
      if (Math.hypot(w.x, w.z) > (map.radius + 2) * WORLD_PER_HEX) continue;
      const gy = ctx.ground(w.x, w.z);
      kit.add(rock, ROCK[Math.floor(rng() * 3)] as string, place(w.x, gy + 0.15, w.z, R() * 3, new THREE.Vector3(0.6 + 0.3 * rng(), 0.45 + 0.3 * rng(), 0.55 + 0.3 * rng())));
      if (rng() < 0.6) kit.add(rock, ROCK[Math.floor(rng() * 3)] as string, place(w.x + 0.4 * R(), gy + 0.1, w.z + 0.4 * R(), R() * 3, 0.35 + 0.2 * rng()));
      this.rim.push(new THREE.Vector3(w.x, gy + 0.25, w.z));
    }

    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.02, flatShading: true });
    const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
    this.mats.push(mat, litMat);
    for (const [k, mm, name] of [
      [kit, mat, 'fin-coast-land'],
      [lit, litMat, 'fin-coast-lamps'],
    ] as const) {
      const mesh = k.build(mm, name);
      if (!mesh) continue;
      mesh.receiveShadow = mm === mat;
      mesh.castShadow = mm === mat && !ctx.low;
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }

    // ---- The sea: one wide sheet past the rim on the sea side (the shore's sea, turned to face out).
    let top = -Infinity;
    for (const p of this.rim) top = Math.max(top, p.y - 0.25);
    if (!Number.isFinite(top)) top = ctx.ground(0, 0);
    const big = (map.radius + 22) * WORLD_PER_HEX;
    const seaG = new THREE.PlaneGeometry(big * 2, big * 2, 64, 64).rotateX(-Math.PI / 2);
    this.geos.push(seaG);
    this.seaM = seaMat({ x: -r.dir.x, y: -r.dir.y });
    this.seaM.uniforms.front!.value = -seaAt;
    this.seaM.uniforms.foam!.value = 380;
    this.mats.push(this.seaM);
    this.sea = new THREE.Mesh(seaG, this.seaM);
    this.sea.position.y = top + 0.12;
    this.sea.name = 'fin-sea';
    this.sea.renderOrder = 3;
    this.sea.frustumCulled = false;
    this.group.add(this.sea);

    // ---- The titan's fore-fin, far out at sea: a vast, dark, barnacled flipper, mostly under the water.
    {
      const shape = new THREE.Shape();
      shape.moveTo(-6.5, 0);
      shape.bezierCurveTo(-6.2, 4.5, -3.5, 9.5, 0.6, 12.5);
      shape.bezierCurveTo(1.8, 13.2, 2.6, 12.4, 2.4, 11.2);
      shape.bezierCurveTo(2.6, 7.5, 4.6, 3.5, 6.5, 0);
      shape.lineTo(-6.5, 0);
      const g = new THREE.ExtrudeGeometry(shape, { depth: 1.6, bevelEnabled: true, bevelThickness: 0.6, bevelSize: 0.5, bevelSegments: 2, curveSegments: 14 });
      g.translate(0, 0, -0.8);
      // A ridge down its face and barnacle crusts, merged into it.
      const fk = new Kit();
      fk.add(g, '#262c32', new THREE.Matrix4());
      for (let i = 0; i < 46; i++) {
        const t = rng();
        const y = 0.6 + t * 10.5;
        const half = 6.2 * (1 - t) + 0.8;
        const x = (rng() * 2 - 1) * half * 0.85 + t * 1.2;
        const z = (rng() < 0.5 ? -1 : 1) * (1.3 + 0.1 * rng());
        fk.add(ball, rng() < 0.5 ? '#6a6a60' : '#4a5048', place(x, y, z, 0, new THREE.Vector3(0.35 + 0.4 * rng(), 0.25 + 0.3 * rng(), 0.2)));
      }
      for (let i = 0; i < 9; i++) fk.add(box, '#3a4248', place(0.4 + i * 0.12, 1 + i * 1.15, 1.2, 0, new THREE.Vector3(0.4, 1.0, 0.25), 0, -0.12));
      g.dispose();
      const finMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.62, metalness: 0.05, flatShading: true });
      this.mats.push(finMat);
      this.fin = fk.build(finMat, 'titan-fin') as THREE.Mesh;
      this.geos.push(this.fin.geometry);
      const s0 = sim(seaAt - 6500, 0);
      const w = ctx.at(s0.x, s0.y, 0);
      // Mostly under the water: a dark ridge at rest, a looming wall when it rises (the shape is ~12.5 tall, scaled).
      this.finUp = this.sea.position.y - (mode === 'big' ? 10.5 : 13.5);
      this.finRest = this.sea.position.y - 18.5;
      this.fin.position.set(w.x, this.finRest, w.z);
      // Its broad face turned to the land.
      this.fin.rotation.y = Math.atan2(dx, dy);
      this.fin.scale.setScalar(1.6);
      this.fin.frustumCulled = false;
      this.group.add(this.fin);
    }

    // ---- The ropes: a hero clinging to a post.
    const ropeG = new THREE.CylinderGeometry(0.07, 0.07, 1, 6).translate(0, 0.5, 0);
    const ropeM = new THREE.MeshBasicMaterial({ color: '#d8c49a' });
    this.geos.push(ropeG);
    this.mats.push(ropeM);
    this.ropes = new THREE.InstancedMesh(ropeG, ropeM, MAX_ROPES);
    this.ropes.count = 0;
    this.ropes.frustumCulled = false;
    this.ropes.name = 'fin-ropes';
    this.group.add(this.ropes);

    // ---- La Orilla 5: the dragon's skull, to break the surface where its fire will come from.
    if (mode === 'breath') {
      const sk = new Kit();
      const eyes = new Kit();
      const cone = new THREE.ConeGeometry(0.5, 1, 5);
      // A long skull of black bone facing +Z (the land): the cranium, a long snout, deep sockets with a coal in each,
      // pale brow ridges, two long horns swept back and up, teeth down both sides, coral on the crown.
      sk.add(ball, '#3a3432', place(0, 0.35, -0.25, 0, new THREE.Vector3(1.15, 0.75, 1.35)));
      sk.add(ball, '#2c2726', place(0, 0.28, 0.85, 0, new THREE.Vector3(0.72, 0.45, 1.7)));
      sk.add(ball, '#1c1818', place(0, 0.02, 0.8, 0, new THREE.Vector3(0.62, 0.3, 1.5)));
      sk.add(cone, '#b0a898', place(0, 0.2, 1.75, 0, new THREE.Vector3(0.22, 0.38, 0.22), Math.PI / 2 + 0.35));
      for (const k of [-1, 1]) {
        sk.add(ball, '#0c0a0c', place(k * 0.36, 0.55, 0.2, 0, new THREE.Vector3(0.34, 0.26, 0.38)));
        eyes.add(ball, '#ffb070', place(k * 0.38, 0.56, 0.3, 0, 0.16));
        sk.add(cone, '#b0a898', place(k * 0.38, 0.72, 0.1, 0, new THREE.Vector3(0.14, 0.6, 0.14), Math.PI / 2 - 0.25, k * -0.5));
        sk.add(cone, '#c9c0b0', place(k * 0.45, 0.85, -0.85, 0, new THREE.Vector3(0.22, 1.9, 0.22), -1.15, k * -0.22));
        sk.add(cone, '#8f877c', place(k * 0.55, 0.3, -0.6, 0, new THREE.Vector3(0.14, 0.8, 0.14), -1.4, k * -0.7));
        for (let i = 0; i < 6; i++) sk.add(cone, '#c9c0b0', place(k * (0.3 - i * 0.025), 0.02, 0.3 + i * 0.26, 0, new THREE.Vector3(0.08, 0.3, 0.08), Math.PI));
      }
      for (let i = 0; i < 5; i++) sk.add(cone, i % 2 ? '#b4483c' : '#d7877a', place(0.25 * R(), 0.95, -0.3 + 0.25 * R(), R() * 3, new THREE.Vector3(0.1, 0.32, 0.1), R() * 0.8, R() * 0.8));
      eyes.add(ball, '#ff8a3a', place(0, 0.12, 1.2, 0, new THREE.Vector3(0.4, 0.14, 0.6)));
      const skM = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.05, flatShading: true });
      const eyeM = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
      this.mats.push(skM, eyeM);
      this.skull = new THREE.Group();
      this.skull.name = 'sunken-skull';
      for (const [k, mm] of [[sk, skM], [eyes, eyeM]] as const) {
        const mesh = k.build(mm, 'sunken-skull');
        if (!mesh) continue;
        this.geos.push(mesh.geometry);
        this.skull.add(mesh);
      }
      this.skull.scale.setScalar(1.4);
      // Yawed first, then reared about its own cross axis.
      this.skull.rotation.order = 'YXZ';
      this.skull.visible = false;
      this.group.add(this.skull);
      cone.dispose();
    }

    this.group.add(this.glow.mesh);
    for (const g of [box, cyl, rock, ball, torus]) g.dispose();
    this.group.updateMatrixWorld(true);
    this.sky = captureSky(ctx);
  }

  /** Darkens the terrain past the rim into wet rock, and clears what grows out there (under the sea). */
  apply(terrain: TerrainBuild): void {
    const dx = this.r.dir.x / 1000;
    const dy = this.r.dir.y / 1000;
    const edge = -(this.map.bastion + 0.55) * 1000;
    const inOf = (wx: number, wz: number): number => (wx / K) * dx + (wz / K) * dy;
    const c = new THREE.Color();
    const tint = (mesh: THREE.Mesh): void => {
      const geo = mesh.geometry;
      const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!col || !pos) return;
      mesh.updateMatrixWorld(true);
      const p = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        const t = (edge + 900 - inOf(p.x, p.z)) / 900;
        if (t <= 0) continue;
        c.setRGB(col.getX(i), col.getY(i), col.getZ(i)).lerp(WET, Math.min(1, t) * 0.8);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    };
    tint(terrain.mesh);
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') tint(o as THREE.Mesh);
      const im = o as THREE.InstancedMesh;
      if (!im.isInstancedMesh || o.name === 'rocks') return;
      im.updateMatrixWorld(true);
      let hid = false;
      for (let i = 0; i < im.count; i++) {
        im.getMatrixAt(i, m);
        p.setFromMatrixPosition(m).applyMatrix4(im.matrixWorld);
        if (inOf(p.x, p.z) > edge + 300) continue;
        im.setMatrixAt(i, zero);
        hid = true;
      }
      if (hid) im.instanceMatrix.needsUpdate = true;
    });
  }

  /** Every fresh tick: the fin's moments, the surf on the rocks, water streaming off the fin, heroes thrown. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const r = this.r;
    for (const e of s._events ?? []) {
      if (e.t !== 'fin') continue;
      const ln = e.lane >= 0 ? this.lanes[e.lane] : undefined;
      if (this.mode === 'breath' && ln && (e.kind === 'warn' || e.kind === 'sweep')) {
        this.breathEvent(s, e.kind, e.lane, ln);
        continue;
      }
      if (e.kind === 'warn' && ln) {
        // Far out the fin stirs: the sea draws back from the lane, a deep rumble.
        this.ctx.flare(ln.sea.clone().setY(this.sea.position.y + 0.8), '#ff4f3a', 3, 20);
        this.ctx.vfx.burst(ln.sea.clone().setY(this.sea.position.y + 0.2), { n: low ? 6 : 16, color: '#dff0ff', speed: 0.06, up: 0.08, size: 0.1, life: 20, gravity: 0.01, spread: 1.4 });
        this.ctx.shake(0.12);
      } else if (e.kind === 'sweep' && ln) {
        this.swept = { lane: e.lane, at: this.now };
        this.ctx.shake(this.mode === 'big' ? 0.95 : 0.55);
        if (this.mode === 'big') {
          // Over the dragon the fin comes down like a mountain: spray all along it, a second wall of it.
          this.ctx.flare(ln.land.clone().lerp(ln.sea, 0.5).setY(this.sea.position.y + 3), '#e6f2ff', 3.5, 18);
          this.ctx.vfx.ring(ln.sea.clone().lerp(ln.land, 0.35).setY(this.sea.position.y + 0.3), 0.5, 5, '#e6f2ff', 20, 0.8);
        }
        // White water all down the lane.
        const n = low ? 6 : 14;
        for (let k = 0; k < n; k++) {
          this.v.lerpVectors(ln.sea, ln.land, k / (n - 1));
          this.v.y = this.ctx.ground(this.v.x, this.v.z) + 0.25;
          this.ctx.vfx.burst(this.v, { n: low ? 3 : 7, color: k % 2 ? '#ffffff' : '#cfe8ff', speed: 0.08, up: 0.12, size: 0.12, life: 22, gravity: 0.012, spread: 0.9 });
        }
        this.ctx.flare(ln.sea.clone().setY(this.sea.position.y + 1.5), '#e8f4ff', 4, 18);
        // Heroes just outside the lane, not by a post: thrown out of it, a splash where they land.
        const lane = r.lanes[e.lane] ?? 0;
        for (const h of s.heroes) {
          if (h.downTicks > 0) continue;
          const a = Math.abs((-h.x * r.dir.y + h.y * r.dir.x) / 1000 - lane);
          if (a < r.half - 50 || a > r.half + r.throw + 500) continue;
          const hp = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
          if (this.posts.some((p) => Math.hypot(p.x - hp.x, p.z - hp.z) < (r.hold + 200) * K)) continue;
          this.v.copy(hp).setY(this.ctx.ground(hp.x, hp.z) + 0.1);
          this.ctx.vfx.burst(this.v, { n: low ? 5 : 12, color: '#dff0ff', speed: 0.06, up: 0.09, size: 0.08, life: 16, gravity: 0.014, spread: 0.5 });
          this.ctx.vfx.ring(this.v, 0.2, 1.1, '#cfe8ff', 10, 0.7);
        }
      } else if (e.kind === 'dawn') {
        this.ctx.flare(this.ctx.at(0, 0, 2.4), '#ffb08a', 5, 30);
      }
    }
    // The surf breaking on the rim's rocks.
    if (this.rim.length && s.tick % (low ? 6 : 3) === 0) {
      const p = this.rim[(s.tick * 7) % this.rim.length] as THREE.Vector3;
      this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: '#ffffff', speed: 0.04, up: 0.08, size: 0.09, life: 14, gravity: 0.012, spread: 0.5 });
    }
    // Water streaming off the fin while it is up.
    if (this.raise > 0.25 && s.tick % (low ? 4 : 2) === 0) {
      const f = this.fin.position;
      this.v.set(f.x + (Math.sin(s.tick * 1.7) * 4.5), f.y + 6 + 5 * this.raise * Math.abs(Math.cos(s.tick * 0.9)), f.z);
      this.ctx.vfx.burst(this.v, { n: low ? 2 : 5, color: '#e6f2ff', speed: 0.02, up: -0.02, size: 0.16, life: 26, gravity: 0.018, spread: 1.2 });
    }
  }

  /** Every redraw: the lanes' warning and sweep, the fin's height, the ropes, the lamps and the light of the hour. */
  draw(s: BattleState): void {
    const r = this.r;
    const st = s.fin;
    const marked = st && st.lane >= 0 ? st.lane : -1;
    const warn = marked >= 0 && st ? Math.min(1, Math.max(0.02, 1 - (st.next - s.waveT) / Math.max(1, r.warn))) : 0;
    const since = this.swept ? this.now - this.swept.at : Infinity;
    const sweep = since < SWEEP_S * 1.6 ? since / SWEEP_S : -1;
    this.lanes.forEach((l, i) => {
      l.mat.uniforms.fire!.value = this.mode === 'breath' ? 1 : 0;
      l.mat.uniforms.warn!.value = i === marked ? warn : 0;
      l.mat.uniforms.sweep!.value = this.swept && i === this.swept.lane ? sweep : -1;
      l.mat.uniforms.time!.value = this.now;
    });
    // The fin: up as the lane is marked, down with the wave.
    const down = since < SWEEP_S ? 1 - since / SWEEP_S : 0;
    const want = marked >= 0 ? easeOut(warn) : 0;
    this.raise = this.mode === 'breath' ? 0 : Math.max(want, since < SWEEP_S ? down : 0);
    // The dragon's skull: up out of the sea at the marked lane as its fire comes, back under after.
    if (this.skull) {
      const at = marked >= 0 ? marked : this.swept && since < SWEEP_S * 1.4 ? this.swept.lane : -1;
      const ln = at >= 0 ? this.lanes[at] : undefined;
      const up = marked >= 0 ? easeOut(warn) : ln ? 1 - Math.min(1, since / (SWEEP_S * 1.4)) : 0;
      this.skull.visible = !!ln && up > 0.01;
      if (ln) {
        // Out from the rim, facing up the lane, the jaw rearing as it breathes.
        this.v.lerpVectors(ln.land, ln.sea, 1.08);
        this.skull.position.set(this.v.x, this.sea.position.y - 1.6 + 1.9 * up, this.v.z);
        this.skull.rotation.set(-0.25 * up - (since < SWEEP_S ? 0.25 : 0), Math.atan2(ln.land.x - ln.sea.x, ln.land.z - ln.sea.z), 0);
      }
    }
    this.fin.position.y = this.finRest + (this.finUp - this.finRest) * this.raise;
    this.fin.rotation.x = -0.18 * this.raise;

    // The hour: day, then a moonlit night, then a warm dawn.
    const night = Math.min(1, Math.max(0, (s.waveT - r.night) / 180));
    const dawn = st?.done ? Math.min(1, (s.tick - st.done) / 30) : s.waveT >= r.dawn ? 1 : 0;
    paintHour(this.ctx, this.sky, night, dawn);
    this.seaM.uniforms.night!.value = night * (1 - dawn * 0.7);
    this.seaM.uniforms.time!.value = this.now;
    for (const l of this.lanes) l.mat.uniforms.night!.value = night * (1 - dawn * 0.6);

    this.glow.begin();
    for (const l of this.lamps) this.glow.add(l.x, l.y, l.z, LAMP, 0.6 + 0.5 * night, 0.4 + 0.45 * night * (1 - dawn));
    if (this.skull?.visible) {
      const p = this.skull.position;
      this.glow.add(p.x, p.y + 0.5, p.z, RED, 3.2, 0.25 + 0.2 * Math.sin(this.now * 8));
    }
    // The posts in a marked lane: a red glint on their heads, so a hero sees where to hold on (not against fire).
    if (marked >= 0 && this.mode !== 'breath') {
      const pulse = 0.5 + 0.5 * Math.sin(this.now * 9);
      const lane = r.lanes[marked] ?? 0;
      r.posts.forEach((p, i) => {
        if (Math.abs((-p.x * r.dir.y + p.y * r.dir.x) / 1000 - lane) > r.half) return;
        const w = this.posts[i] as THREE.Vector3;
        this.glow.add(w.x, w.y + 0.15, w.z, RED, 0.9, 0.45 + 0.4 * pulse);
      });
    }

    // A rope from each post to the hero holding on to it, while a lane is marked or the wave goes by (a post is no use against fire).
    let n = 0;
    if ((marked >= 0 || since < SWEEP_S * 1.5) && this.mode !== 'breath') {
      for (const h of s.heroes) {
        if (h.downTicks > 0 || n >= MAX_ROPES) continue;
        const hp = this.ctx.pos(h.id);
        if (!hp) continue;
        for (const p of this.posts) {
          if (Math.hypot(p.x - hp.x, p.z - hp.z) > r.hold * K) continue;
          // From the pile's top ring out to the hero's hands, a little slack above them.
          this.v.copy(p).setY(p.y - 0.1);
          this.v2.copy(hp).setY(hp.y + 0.85).sub(this.v);
          const len = this.v2.length();
          if (len < 0.05) break;
          this.q.setFromUnitVectors(UPV, this.v2.normalize());
          this.ropes.setMatrixAt(n++, this.m.compose(this.v, this.q, this.s3.set(1, len, 1)));
          // A cold ring of spray round the one holding on.
          this.glow.add(hp.x, hp.y + 0.05, hp.z, CLING, 0.9, 0.55 + 0.3 * Math.sin(this.now * 10));
          break;
        }
      }
    }
    this.glow.end();
    this.ropes.count = n;
    this.ropes.instanceMatrix.needsUpdate = true;
  }

  /** La Orilla 5's fire: the skull surfacing and smoke off the water at the warning; the sheet of fire on the sweep. */
  private breathEvent(s: BattleState, kind: 'warn' | 'sweep', lane: number, ln: { sea: THREE.Vector3; land: THREE.Vector3 }): void {
    const low = this.ctx.low;
    if (kind === 'warn') {
      this.v.copy(ln.sea).setY(this.sea.position.y + 0.3);
      this.ctx.vfx.burst(this.v, { n: low ? 6 : 16, color: '#dff0ff', speed: 0.07, up: 0.1, size: 0.12, life: 22, gravity: 0.012, spread: 1.2 });
      this.ctx.vfx.burst(this.v, { n: low ? 2 : 5, color: '#8a8a90', speed: 0.02, up: 0.04, size: 0.7, life: 40, smoke: true, spread: 0.8 });
      this.ctx.flare(this.v.clone().setY(this.v.y + 1), '#ff6a2a', 3, 18);
      this.ctx.shake(0.15);
      return;
    }
    this.swept = { lane, at: this.now };
    this.ctx.shake(0.4);
    const n = low ? 6 : 14;
    for (let k = 0; k < n; k++) {
      this.v.lerpVectors(ln.sea, ln.land, k / (n - 1));
      this.v.y = this.ctx.ground(this.v.x, this.v.z) + 0.3;
      this.ctx.vfx.burst(this.v, { n: low ? 3 : 7, color: k % 2 ? '#ffb070' : '#ff6a2a', speed: 0.06, up: 0.1, size: 0.14, life: 20, gravity: -0.004, spread: 0.9 });
      if (k % 3 === 0) this.ctx.vfx.burst(this.v, { n: 1, color: '#3a3030', speed: 0.01, up: 0.05, size: 0.8, life: 46, smoke: true, spread: 0.6 });
    }
    this.ctx.flare(ln.sea.clone().setY(this.sea.position.y + 1.5), '#ffb070', 5, 18);
    // The heroes caught in it: flames off them.
    const r = this.r;
    const off = r.lanes[lane] ?? 0;
    for (const h of s.heroes) {
      if (h.downTicks > 0) continue;
      if (Math.abs((-h.x * r.dir.y + h.y * r.dir.x) / 1000 - off) > r.half) continue;
      const hp = this.ctx.pos(h.id) ?? this.ctx.at(h.x, h.y, 0);
      this.v.copy(hp).setY(hp.y + 0.6);
      this.ctx.vfx.burst(this.v, { n: low ? 5 : 12, color: '#ff8a3a', speed: 0.05, up: 0.1, size: 0.1, life: 18, gravity: -0.004, spread: 0.4 });
    }
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}

const UPV = new THREE.Vector3(0, 1, 0);

const easeOut = (t: number): number => 1 - (1 - t) * (1 - t);

/** A small seeded random (the layout is the same every time for the same coast). */
const mulberry = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
